import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationPicker from "@/components/LocationPicker";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, MapPin, Users, Trash2, Edit2, Plus, BarChart3 } from "lucide-react";

/**
 * Feb 2026 — DINAS management page.
 *
 * DINAS = konteks lokasi kerja (bukan status absensi baru). Saat teknisi
 * memiliki DINAS aktif pada tanggal tertentu, target GPS check-in
 * berpindah ke koordinat DINAS. Halaman ini memungkinkan admin & koord
 * (dalam scope wilayah) untuk mengelola daftar DINAS.
 */

const todayWibISO = () => {
  // Approximate WIB date on client (UTC+7)
  const now = new Date();
  const wib = new Date(now.getTime() + (now.getTimezoneOffset() + 420) * 60000);
  return wib.toISOString().slice(0, 10);
};

const emptyForm = () => ({
  start_date: todayWibISO(),
  end_date: todayWibISO(),
  tujuan: "",
  keperluan: "",
  user_ids: [],
  latitude: null,
  longitude: null,
  radius_meter: 150,
  accuracy_threshold_meter: 75,
});

export default function AttendanceDuties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdminOrKoord = user?.role === "admin" || user?.role === "koordinator";
  const [duties, setDuties] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/duties", { params: { include_stats: true } });
      setDuties(data.items || []);
    } catch (e) {
      toast.error("Gagal memuat DINAS");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/users?role=teknisi").then(({ data }) => {
      const items = Array.isArray(data) ? data : data.items || [];
      setTechnicians(items);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpenForm(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      start_date: d.start_date || d.duty_date,
      end_date: d.end_date || d.duty_date,
      tujuan: d.tujuan || "",
      keperluan: d.keperluan || "",
      user_ids: [...(d.user_ids || [])],
      latitude: d.latitude,
      longitude: d.longitude,
      radius_meter: d.radius_meter || 150,
      accuracy_threshold_meter: d.accuracy_threshold_meter || 75,
    });
    setOpenForm(true);
  };

  const submit = async () => {
    if (!form.tujuan.trim()) return toast.error("Tujuan wajib diisi");
    if (!form.start_date) return toast.error("Tanggal mulai wajib diisi");
    if (!form.end_date) return toast.error("Tanggal selesai wajib diisi");
    if (form.end_date < form.start_date) return toast.error("Tanggal selesai tidak boleh sebelum tanggal mulai");
    if (form.latitude == null || form.longitude == null) return toast.error("Lokasi wajib ditentukan di peta");
    if (!form.user_ids.length) return toast.error("Minimal 1 peserta");
    try {
      if (editing) {
        await api.patch(`/attendance/duties/${editing.id}`, form);
        toast.success("DINAS diperbarui");
      } else {
        await api.post("/attendance/duties", form);
        toast.success("DINAS dibuat");
      }
      setOpenForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan DINAS");
    }
  };

  const cancelDuty = async (d) => {
    if (!confirm(`Batalkan DINAS ke ${d.tujuan}?`)) return;
    try {
      await api.delete(`/attendance/duties/${d.id}`);
      toast.success("DINAS dibatalkan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membatalkan");
    }
  };

  const toggleParticipant = (uid) => {
    setForm((f) => {
      const has = f.user_ids.includes(uid);
      return { ...f, user_ids: has ? f.user_ids.filter((x) => x !== uid) : [...f.user_ids, uid] };
    });
  };

  const techMap = useMemo(() => {
    const m = {};
    for (const t of technicians) m[t.id] = t.name || t.username;
    return m;
  }, [technicians]);

  return (
    <div className="space-y-6" data-testid="attendance-duties-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Absensi</div>
          <h1 className="text-2xl font-black text-[#0F172A] flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#0055FF]" /> Dinas / Kerja di Lokasi Lain
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Atur target GPS validasi absensi ketika tim bekerja di luar kantor (rapat, kunjungan vendor, dll).
            Status absensi tetap HADIR/TERLAMBAT — DINAS hanya konteks lokasi.
          </p>
        </div>
        {isAdminOrKoord && (
          <Button onClick={openCreate} data-testid="btn-create-duty" className="bg-[#0055FF] hover:bg-[#0044CC]">
            <Plus className="w-4 h-4 mr-1" /> Buat DINAS
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-[#6B7280]">Memuat…</div>}
        {!loading && duties.length === 0 && (
          <div className="text-sm text-[#6B7280] p-8 text-center border rounded-md" data-testid="duties-empty">
            Belum ada penugasan DINAS.
          </div>
        )}
        {duties.map((d) => {
          const startDate = d.start_date || d.duty_date;
          const endDate = d.end_date || d.duty_date;
          const isMultiDay = startDate !== endDate;
          const today = todayWibISO();
          const isPast = endDate < today;
          const isCancelled = !d.active;
          const isOngoing = !isCancelled && !isPast && startDate <= today;
          // Feb 2026 — Klik card → Rekap Absensi terfilter duty_id + periode.
          // Hanya untuk admin/koord (privilege `attendance_recap_view`);
          // teknisi tidak punya akses ke /attendance-report.
          const openRekap = () => {
            if (!isAdminOrKoord) return;
            const params = new URLSearchParams({
              duty_id: d.id,
              start_date: startDate,
              end_date: endDate,
            });
            navigate(`/attendance-report?${params.toString()}`);
          };
          const cardClickable = isAdminOrKoord;
          return (
            <div key={d.id} data-testid={`duty-card-${d.id}`}
              onClick={cardClickable ? openRekap : undefined}
              onKeyDown={cardClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openRekap(); } } : undefined}
              role={cardClickable ? "button" : undefined}
              tabIndex={cardClickable ? 0 : undefined}
              className={`bg-white border rounded-lg p-4 ${isCancelled ? "opacity-60 border-[#E5E7EB]" : "border-[#E5E7EB] hover:border-[#93C5FD]"} ${cardClickable ? "cursor-pointer hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]" : ""} transition-colors`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
                      isCancelled ? "bg-[#FEE2E2] text-[#991B1B]" :
                      isOngoing ? "bg-[#DCFCE7] text-[#166534]" :
                      isPast ? "bg-[#E5E7EB] text-[#4B5563]" : "bg-[#DBEAFE] text-[#1E40AF]"
                    }`}>
                      {isCancelled ? "Dibatalkan" : isOngoing ? "Berlangsung" : isPast ? "Lampau" : "Akan Datang"}
                    </span>
                    <span className="text-sm font-mono text-[#4B5563]">
                      {isMultiDay ? `${startDate} → ${endDate}` : startDate}
                    </span>
                  </div>
                  <div className="mt-1 text-lg font-black text-[#0F172A] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#0055FF] shrink-0" />
                    {d.tujuan}
                  </div>
                  {d.keperluan && <div className="text-sm text-[#4B5563] mt-0.5">{d.keperluan}</div>}
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-[#6B7280]">
                    <Users className="w-3.5 h-3.5" />
                    <span>{(d.user_ids || []).length} peserta:</span>
                    <span className="text-[#0F172A] font-semibold">
                      {(d.user_ids || []).map((uid) => techMap[uid] || uid.slice(0, 8)).join(", ")}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1 font-mono">
                    Target GPS: {Number(d.latitude).toFixed(5)}, {Number(d.longitude).toFixed(5)} · radius {d.radius_meter}m
                  </div>
                  {/* Feb 2026 — Ringkasan Absensi per DINAS. Chip hanya
                      tampil bila backend sudah mengirim `stats` (opt-in
                      via include_stats=true) supaya card tidak crash pada
                      legacy response. */}
                  {d.stats && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`duty-stats-${d.id}`}>
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Absensi:</span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] text-[10px] font-bold" title="Hadir">
                        Hadir · {d.stats.HADIR || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#FDE68A] bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold" title="Terlambat">
                        Terlambat · {d.stats.TERLAMBAT || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#FCA5A5] bg-[#FEE2E2] text-[#7F1D1D] text-[10px] font-bold" title="Alpa">
                        Alpa · {d.stats.ALPA || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#BFDBFE] bg-[#DBEAFE] text-[#1E3A8A] text-[10px] font-bold" title="Izin">
                        Izin · {d.stats.IZIN || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#FECDD3] bg-[#FFE4E6] text-[#9F1239] text-[10px] font-bold" title="Sakit">
                        Sakit · {d.stats.SAKIT || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded border border-[#D8B4FE] bg-[#E9D5FF] text-[#6B21A8] text-[10px] font-bold" title="Cuti">
                        Cuti · {d.stats.CUTI || 0}
                      </span>
                    </div>
                  )}
                </div>
                {isAdminOrKoord && (
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openRekap(); }} data-testid={`btn-rekap-duty-${d.id}`} title="Lihat Rekap Absensi DINAS ini" className="text-[#0055FF]">
                      <BarChart3 className="w-3.5 h-3.5" />
                    </Button>
                    {!isCancelled && (
                      <>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openEdit(d); }} data-testid={`btn-edit-duty-${d.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); cancelDuty(d); }} data-testid={`btn-cancel-duty-${d.id}`} className="text-[#B91C1C]">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duty-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit DINAS" : "Buat DINAS Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Tanggal Mulai</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))} data-testid="duty-start-date" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Tanggal Selesai</label>
                <Input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="duty-end-date" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Radius (m)</label>
                <Input type="number" value={form.radius_meter} onChange={(e) => setForm((f) => ({ ...f, radius_meter: parseInt(e.target.value, 10) || 150 }))} data-testid="duty-radius" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Akurasi GPS (m)</label>
                <Input type="number" value={form.accuracy_threshold_meter} onChange={(e) => setForm((f) => ({ ...f, accuracy_threshold_meter: parseInt(e.target.value, 10) || 75 }))} data-testid="duty-accuracy" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Tujuan</label>
              <Input value={form.tujuan} onChange={(e) => setForm((f) => ({ ...f, tujuan: e.target.value }))} placeholder="Contoh: Purwokerto" data-testid="duty-tujuan" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280]">Keperluan</label>
              <Input value={form.keperluan} onChange={(e) => setForm((f) => ({ ...f, keperluan: e.target.value }))} placeholder="Contoh: Rapat Upstream" data-testid="duty-keperluan" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280] mb-1 block">Peserta ({form.user_ids.length})</label>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 grid grid-cols-2 gap-1">
                {technicians.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F8FAFC] rounded px-1">
                    <input type="checkbox" checked={form.user_ids.includes(t.id)} onChange={() => toggleParticipant(t.id)} data-testid={`duty-participant-${t.id}`} />
                    <span>{t.name || t.username}</span>
                  </label>
                ))}
                {technicians.length === 0 && <div className="col-span-2 text-xs text-[#6B7280] text-center py-2">Tidak ada teknisi tersedia</div>}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-[#6B7280] mb-1 block">Lokasi Target (klik "Ubah Lokasi" lalu geser peta)</label>
              <LocationPicker
                value={form.tujuan}
                lat={form.latitude}
                lng={form.longitude}
                onChange={({ lat, lng }) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setOpenForm(false)} data-testid="duty-cancel">Batal</Button>
              <Button onClick={submit} data-testid="duty-submit" className="bg-[#0055FF] hover:bg-[#0044CC]">
                {editing ? "Simpan Perubahan" : "Buat DINAS"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
